import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Table, TableBody, TableHead, TableRow, TableCell,
  Typography, Box, Alert, CircularProgress,
} from '@mui/material';
import http from '../../../utils/http';
import { errorToastMessage } from '../../../utils/toast';

interface HardDeletePreviewRow {
  table: string;
  rowCount: number;
}

interface HardDeletePreview {
  participantId: string;
  name: string;
  email: string;
  redactedAt: string;
  gracePeriodEnds: string;
  tableRowCounts: HardDeletePreviewRow[];
  warningMessage: string;
  tier: 'registry' | 'study';
  studyId: string | null;
}

interface Props {
  open: boolean;
  participantId: string;
  /** Phase 6c — when set, targets the study-tier hard-delete endpoints. */
  studyId?: string;
  onClose: () => void;
  onDeleted: () => void;
}

type ModalState = 'loading' | 'loaded' | 'confirming' | 'error';

const HardDeleteConfirmModal: React.FC<Props> = ({
  open, participantId, studyId, onClose, onDeleted,
}) => {
  const [state, setState] = useState<ModalState>('loading');
  const [preview, setPreview] = useState<HardDeletePreview | null>(null);

  const previewUrl = studyId
    ? `/admin/participants/${participantId}/studies/${studyId}/hard-delete/preview`
    : `/admin/participants/${participantId}/hard-delete/preview`;
  const deleteUrl = studyId
    ? `/admin/participants/${participantId}/studies/${studyId}/hard-delete`
    : `/admin/participants/${participantId}/hard-delete`;

  const loadPreview = async () => {
    setState('loading');
    try {
      const res = await http.get(previewUrl);
      setPreview(res.data.data);
      setState('loaded');
    } catch (err) {
      setState('error');
      errorToastMessage(err as Error);
    }
  };

  useEffect(() => {
    if (open) loadPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleConfirm = async () => {
    setState('confirming');
    try {
      await http.delete(deleteUrl);
      onDeleted();
    } catch (err) {
      setState('loaded');
      errorToastMessage(err as Error);
    }
  };

  const handleClose = () => {
    if (state === 'confirming') return;
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {studyId
          ? 'Permanently Delete Study Redaction'
          : 'Permanently Delete Participant'}
      </DialogTitle>
      <DialogContent>
        {preview && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="body2">
              <strong>Participant ID:</strong> {preview.participantId}
            </Typography>
            <Typography variant="body2">
              <strong>Name:</strong> {preview.name}
            </Typography>
            <Typography variant="body2">
              <strong>Email:</strong> {preview.email}
            </Typography>
            <Typography variant="body2">
              <strong>Redacted On:</strong>{' '}
              {new Date(preview.redactedAt).toLocaleDateString()}
            </Typography>
            <Typography variant="body2">
              <strong>Grace Period Ended:</strong>{' '}
              {new Date(preview.gracePeriodEnds).toLocaleDateString()}
            </Typography>
          </Box>
        )}

        <Alert severity="error" sx={{ mb: 2 }}>
          {preview?.warningMessage ??
            "This action is PERMANENT and IRREVERSIBLE. The participant's encrypted escrow will be destroyed."}
        </Alert>

        {state === 'loading' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        )}

        {state === 'error' && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              Failed to load preview. Please try again.
            </Alert>
            <Button variant="outlined" onClick={loadPreview}>
              Retry
            </Button>
          </Box>
        )}

        {(state === 'loaded' || state === 'confirming') && preview && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Rows to be permanently deleted:
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Table</TableCell>
                  <TableCell align="right">Rows</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {preview.tableRowCounts.map((row) => (
                  <TableRow key={row.table}>
                    <TableCell sx={{ fontFamily: 'monospace' }}>
                      {row.table}
                    </TableCell>
                    <TableCell align="right">{row.rowCount}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: 'block' }}
            >
              All related rows are removed via database cascade when the
              participant row is deleted.
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={state === 'confirming'}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleConfirm}
          disabled={state !== 'loaded'}
          startIcon={
            state === 'confirming' ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
        >
          Permanently Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HardDeleteConfirmModal;
